import * as React from "react";
import { createPortal } from "react-dom";
import type { ComponentRenderProps } from "@json-render/react";
import { useBoundProp } from "@json-render/react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@erp/ui/alert-dialog";
import { Button, buttonVariants } from "@erp/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@erp/ui/dialog";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@erp/ui/hover-card";
import { Popover, PopoverContent, PopoverTrigger } from "@erp/ui/popover";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@erp/ui/resizable";
import {
  Sheet,
  SheetContent,
  SheetContentResizable,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@erp/ui/sheet";
import { Tooltip, TooltipContent, TooltipTrigger } from "@erp/ui/tooltip";
import { APP_COMPONENTS } from "../catalog";
import { readProps } from "../props";
import { useAppScopeProps } from "./scope";

/**
 * Overlay components: floating panels, modals, side sheets and split panes.
 *
 * Every portaled surface spreads `useAppScopeProps()` so it keeps the App's
 * theme, as the table's menus do. json-render hands a component its children
 * as one rendered list, so a component that needs a trigger takes the first
 * child as the trigger and the rest as content.
 */

const S = APP_COMPONENTS;

/** A trigger child is wrapped in an inert span: the child is its own control. */
const TRIGGER_WRAPPER = <span className="inline-flex" />;

function triggerRender(trigger: React.ReactNode) {
  return React.cloneElement(TRIGGER_WRAPPER, undefined, trigger);
}

const FOCUSABLE =
  'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';

/** The first child is the trigger; the rest are content. */
function splitTrigger(children: React.ReactNode): {
  trigger: React.ReactNode;
  content: React.ReactNode[];
} {
  const [trigger, ...content] = React.Children.toArray(children);
  return { trigger, content };
}

/**
 * Open state for a Dialog, Sheet or ConfirmDialog. A bound `open` lives in the
 * App's state, so any Button can open it with `setState`; an unbound one is
 * local, seeded by the `open` prop.
 */
function useOverlayOpen(
  open: boolean | undefined,
  binding: string | undefined,
): [boolean, (next: boolean) => void] {
  const [bound, setBound] = useBoundProp<boolean>(open, binding);
  const [local, setLocal] = React.useState(open === true);
  const isOpen = binding ? bound === true : local;
  function setOpen(next: boolean) {
    if (binding) setBound(next);
    else setLocal(next);
  }
  return [isOpen, setOpen];
}

function OpenButton({
  label,
  onOpen,
}: {
  label: string | undefined;
  onOpen: () => void;
}) {
  if (!label) return null;
  return (
    <Button variant="outline" onClick={onOpen}>
      {label}
    </Button>
  );
}

/**
 * Where an `OverlayFooter` renders: the overlay owns a footer node and shares
 * it, so the footer can be a child element yet still pin to the bottom.
 */
const FooterSlotContext = React.createContext<HTMLElement | null>(null);

export function OverlayFooterComponent({ children }: ComponentRenderProps) {
  const slot = React.use(FooterSlotContext);
  return slot ? createPortal(children, slot) : null;
}

const FOOTER_CLASS = "flex shrink-0 justify-end gap-2 empty:hidden";

// ── Popover, Tooltip, HoverCard ─────────────────────────────────────────────

export function PopoverComponent({ element, children }: ComponentRenderProps) {
  const p = readProps(S.Popover.props, element.props);
  const scopeProps = useAppScopeProps();
  const { trigger, content } = splitTrigger(children);
  return (
    <Popover>
      <PopoverTrigger
        nativeButton={false}
        // The child is the control; the wrapper only carries the handlers.
        role="presentation"
        tabIndex={-1}
        render={triggerRender(trigger)}
      />
      <PopoverContent side={p.side} align={p.align ?? "start"} {...scopeProps}>
        <div className="flex flex-col gap-2 text-sm">
          {p.title && <span className="font-medium">{p.title}</span>}
          {content}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function TooltipComponent({ element, children }: ComponentRenderProps) {
  const p = readProps(S.Tooltip.props, element.props);
  const scopeProps = useAppScopeProps();
  const [trigger] = React.Children.toArray(children);
  const descriptionId = React.useId();
  const wrapper = React.useRef<HTMLSpanElement>(null);
  // The child is an App element, so it can't take props from here: point its
  // focusable control at an always-present description instead.
  React.useEffect(() => {
    const control = wrapper.current?.querySelector<HTMLElement>(FOCUSABLE);
    if (!control || !p.content) return;
    const previous = control.getAttribute("aria-describedby");
    control.setAttribute(
      "aria-describedby",
      previous ? `${previous} ${descriptionId}` : descriptionId,
    );
    return () => {
      if (previous) control.setAttribute("aria-describedby", previous);
      else control.removeAttribute("aria-describedby");
    };
  }, [descriptionId, p.content]);
  if (!p.content) return <>{trigger}</>;
  return (
    <Tooltip>
      <span ref={wrapper} className="inline-flex">
        <TooltipTrigger render={triggerRender(trigger)} />
        <span id={descriptionId} className="sr-only">
          {p.content}
        </span>
      </span>
      <TooltipContent side={p.side} {...scopeProps}>
        {p.content}
      </TooltipContent>
    </Tooltip>
  );
}

export function HoverCardComponent({
  element,
  children,
}: ComponentRenderProps) {
  const p = readProps(S.HoverCard.props, element.props);
  const scopeProps = useAppScopeProps();
  const { trigger, content } = splitTrigger(children);
  return (
    <HoverCard>
      <HoverCardTrigger render={triggerRender(trigger)} />
      <HoverCardContent side={p.side} align={p.align} {...scopeProps}>
        <div className="flex flex-col gap-2 text-sm">{content}</div>
      </HoverCardContent>
    </HoverCard>
  );
}

// ── Dialog, ConfirmDialog, Sheet ────────────────────────────────────────────

const DIALOG_WIDTH = {
  sm: "sm:max-w-sm",
  md: "sm:max-w-lg",
  lg: "sm:max-w-3xl",
} as const;

export function DialogComponent({
  element,
  children,
  emit,
  bindings,
}: ComponentRenderProps) {
  const p = readProps(S.Dialog.props, element.props);
  const scopeProps = useAppScopeProps();
  const [open, setOpen] = useOverlayOpen(p.open, bindings?.open);
  const [footer, setFooter] = React.useState<HTMLElement | null>(null);
  return (
    <>
      <OpenButton label={p.triggerLabel} onOpen={() => setOpen(true)} />
      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) emit("close");
        }}
      >
        <DialogContent
          className={`flex max-h-[calc(100dvh-2rem)] flex-col ${DIALOG_WIDTH[p.size ?? "md"]}`}
          {...scopeProps}
        >
          <DialogHeader className="shrink-0">
            <DialogTitle>{p.title}</DialogTitle>
            {p.description && (
              <DialogDescription>{p.description}</DialogDescription>
            )}
          </DialogHeader>
          <FooterSlotContext value={footer}>
            <div className="-mx-1 flex min-h-0 min-w-0 flex-1 flex-col gap-2 overflow-y-auto px-1">
              {children}
            </div>
          </FooterSlotContext>
          <div ref={setFooter} className={FOOTER_CLASS} />
        </DialogContent>
      </Dialog>
    </>
  );
}

export function ConfirmDialogComponent({
  element,
  emit,
  bindings,
}: ComponentRenderProps) {
  const p = readProps(S.ConfirmDialog.props, element.props);
  const scopeProps = useAppScopeProps();
  const [open, setOpen] = useOverlayOpen(p.open, bindings?.open);
  // The Action button is a Close part, so a confirm also arrives here as a
  // close; only a close that isn't a confirm (Cancel, Escape, the backdrop)
  // counts as a cancel.
  const confirming = React.useRef(false);
  function onOpenChange(next: boolean) {
    if (next) return;
    setOpen(false);
    if (confirming.current) confirming.current = false;
    else emit("cancel");
  }
  return (
    <>
      <OpenButton label={p.triggerLabel} onOpen={() => setOpen(true)} />
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogContent {...scopeProps}>
          <AlertDialogHeader>
            <AlertDialogTitle>{p.title}</AlertDialogTitle>
            {p.description && (
              <AlertDialogDescription>{p.description}</AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{p.cancelLabel ?? "Cancel"}</AlertDialogCancel>
            <AlertDialogAction
              className={
                p.tone === "destructive"
                  ? buttonVariants({ variant: "destructive" })
                  : undefined
              }
              onClick={() => {
                confirming.current = true;
                setOpen(false);
                emit("confirm");
              }}
            >
              {p.confirmLabel ?? "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

const RESIZABLE_SHEET_SIZE = 45;

export function SheetComponent({
  element,
  children,
  emit,
  bindings,
}: ComponentRenderProps) {
  const p = readProps(S.Sheet.props, element.props);
  const scopeProps = useAppScopeProps();
  const [open, setOpen] = useOverlayOpen(p.open, bindings?.open);
  const [footer, setFooter] = React.useState<HTMLElement | null>(null);
  const body = (
    <>
      <SheetHeader className="shrink-0 pr-10">
        <SheetTitle>{p.title}</SheetTitle>
        {p.description && <SheetDescription>{p.description}</SheetDescription>}
      </SheetHeader>
      <FooterSlotContext value={footer}>
        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-auto p-3">
          {children}
        </div>
      </FooterSlotContext>
      <div ref={setFooter} className={`${FOOTER_CLASS} border-t p-3`} />
    </>
  );
  const side = p.side ?? "right";
  return (
    <>
      <OpenButton label={p.triggerLabel} onOpen={() => setOpen(true)} />
      <Sheet
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) emit("close");
        }}
      >
        {p.resizable ? (
          <SheetContentResizable
            side={side}
            resizableProps={{
              defaultSize: RESIZABLE_SHEET_SIZE,
              className: "flex flex-col",
            }}
            {...scopeProps}
          >
            {body}
          </SheetContentResizable>
        ) : (
          <SheetContent side={side} {...scopeProps}>
            {body}
          </SheetContent>
        )}
      </Sheet>
    </>
  );
}

// ── Resizable panels ────────────────────────────────────────────────────────

const HANDLE_STYLE = {
  horizontal: { backgroundColor: "var(--border)" },
  vertical: {
    backgroundColor: "var(--border)",
    width: "100%",
    height: "4px",
    cursor: "row-resize",
  },
} as const;

/**
 * The shared handle paints itself with fixed hex colors on hover; these no-ops
 * replace that handler so the App's `--border` and `--ring` tokens apply.
 */
function keepHandleColor() {}

export function ResizablePanelsComponent({
  element,
  children,
}: ComponentRenderProps) {
  const p = readProps(S.ResizablePanels.props, element.props);
  const direction = p.direction ?? "horizontal";
  const panels = React.Children.toArray(children);
  return (
    <ResizablePanelGroup
      direction={direction}
      className="min-w-0 overflow-hidden rounded-lg border border-border"
      style={{ height: p.height ?? 360 }}
    >
      {panels.map((panel, index) => (
        <React.Fragment key={React.isValidElement(panel) ? panel.key : index}>
          {index > 0 && (
            <ResizableHandle
              style={HANDLE_STYLE[direction]}
              onMouseEnter={keepHandleColor}
              onMouseLeave={keepHandleColor}
              className="hover:bg-ring!"
            />
          )}
          {panel}
        </React.Fragment>
      ))}
    </ResizablePanelGroup>
  );
}

export function ResizablePanelComponent({
  element,
  children,
}: ComponentRenderProps) {
  const p = readProps(S.ResizablePanel.props, element.props);
  return (
    <ResizablePanel defaultSize={p.defaultSize} minSize={p.minSize ?? 10}>
      <div className="flex h-full min-w-0 flex-col gap-2 overflow-auto p-3">
        {children}
      </div>
    </ResizablePanel>
  );
}
